/**
 * Hooks de la fonctionnalité « Diffuser un défi en YouTube Short / TikTok » (admin).
 *
 * Réutilise le PIPELINE de rendu YouTube Studio (capture + compo FFmpeg + worker)
 * mais en filmant la CARTE DU DÉFI (`/capture/challenge`) en 9:16 : une ligne
 * `youtube_videos` avec `challenge_id` (migration 0140) → enfilée via
 * `youtube-enqueue` avec `platforms=['youtube'|'tiktok']`. Validation manuelle
 * (aperçu) puis publication, comme le reel d'exercice.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { VideoOverlay } from '@jeraisonne/db/schemas/youtube';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { ytDb } from '@/lib/admin/youtube-queries';

/** Plateformes de diffusion d'un défi (verticales courtes). */
export type ChallengeReelPlatform = 'youtube' | 'tiktok';

// ── Liste admin des défis ────────────────────────────────────────────────────

/** Demande de l'auteur pour la page d'accueil (migration 0726). */
export type ChallengePublicRequest = 'none' | 'pending' | 'approved' | 'rejected';

export function toPublicRequest(v: unknown): ChallengePublicRequest {
  return v === 'pending' || v === 'approved' || v === 'rejected' ? v : 'none';
}

export interface AdminChallengeRow {
  id: string;
  slug: string;
  title: string;
  media_type: 'none' | 'photo' | 'video';
  media_url: string | null;
  thumbnail_url: string | null;
  is_public: boolean;
  is_flagged: boolean;
  /** Demande de l'auteur pour la page d'accueil (migration 0726). */
  public_request: ChallengePublicRequest;
  total_views: number;
  total_attempts: number;
  total_success: number;
  created_at: string;
  exercise_title: string;
  level: string | null;
}

/** Tous les défis (RLS admin, migration 0140), récents d'abord. */
export function useAdminChallenges() {
  return useQuery({
    queryKey: ['admin-challenges'],
    queryFn: async (): Promise<AdminChallengeRow[]> => {
      const { data, error } = await ytDb
        .from('challenge_posts')
        .select(
          'id, challenge_slug, title, media_type, media_url, thumbnail_url, is_public, is_flagged, ' +
            'public_request, total_views, total_attempts, total_success, created_at, ' +
            'exercises!challenge_posts_exercise_id_fkey ( title, chapters!exercises_chapter_id_fkey ( level ) )',
        )
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw new Error(error.message);
      return ((data ?? []) as any[]).map((r) => ({
        id: r.id,
        slug: r.challenge_slug,
        title: r.title,
        media_type: r.media_type ?? 'none',
        media_url: r.media_url,
        thumbnail_url: r.thumbnail_url,
        is_public: r.is_public,
        is_flagged: r.is_flagged,
        public_request: toPublicRequest(r.public_request),
        total_views: r.total_views ?? 0,
        total_attempts: r.total_attempts ?? 0,
        total_success: r.total_success ?? 0,
        created_at: r.created_at,
        exercise_title: r.exercises?.title ?? 'Exercice',
        level: r.exercises?.chapters?.level ?? null,
      }));
    },
  });
}

// ── Un défi (données pour la carte de diffusion) ─────────────────────────────

export interface ChallengeDiffusionData {
  id: string;
  slug: string;
  title: string;
  exercise_id: string;
  attempt_id: string | null;
  media_type: 'none' | 'photo' | 'video';
  media_url: string | null;
  thumbnail_url: string | null;
  is_public: boolean;
  is_flagged: boolean;
  public_request: ChallengePublicRequest;
  /** SKILLCARD de fond choisie (migration 0699), null = celle de la question. */
  background_card_id: string | null;
  level: SchoolLevel | null;
  subject: SubjectSlug | null;
  total_attempts: number;
  total_success: number;
}

export function useChallengeForDiffusion(id: string) {
  return useQuery({
    queryKey: ['admin-challenge', id],
    enabled: !!id,
    queryFn: async (): Promise<ChallengeDiffusionData | null> => {
      const { data, error } = await ytDb
        .from('challenge_posts')
        .select(
          'id, challenge_slug, title, exercise_id, attempt_id, media_type, media_url, ' +
            'thumbnail_url, is_public, is_flagged, public_request, background_card_id, total_attempts, total_success, ' +
            'exercises!challenge_posts_exercise_id_fkey ( chapters!exercises_chapter_id_fkey ( level, subject ) )',
        )
        .eq('id', id)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      const r = data as any;
      return {
        id: r.id,
        slug: r.challenge_slug,
        title: r.title,
        exercise_id: r.exercise_id,
        attempt_id: r.attempt_id ?? null,
        media_type: r.media_type ?? 'none',
        media_url: r.media_url,
        thumbnail_url: r.thumbnail_url ?? null,
        is_public: !!r.is_public,
        is_flagged: !!r.is_flagged,
        public_request: toPublicRequest(r.public_request),
        background_card_id: r.background_card_id ?? null,
        level: (r.exercises?.chapters?.level ?? null) as SchoolLevel | null,
        subject: (r.exercises?.chapters?.subject ?? null) as SubjectSlug | null,
        total_attempts: r.total_attempts ?? 0,
        total_success: r.total_success ?? 0,
      };
    },
  });
}

// ── Rendu / publication ──────────────────────────────────────────────────────

/** Invoque `youtube-enqueue` avec un message d'erreur lisible. */
async function enqueueChallengeReel(
  videoId: string,
  platforms: ChallengeReelPlatform[],
  mode: 'preview' | 'publish',
): Promise<void> {
  const { error } = await supabase.functions.invoke('youtube-enqueue', {
    body: { video_id: videoId, platforms, privacy: 'public', mode },
  });
  if (error) {
    let message = error.message;
    try {
      const ctxBody = await (
        error as { context?: { json?: () => Promise<unknown> } }
      ).context?.json?.();
      const obj = ctxBody as { error?: string; message?: string } | undefined;
      message = obj?.message ?? obj?.error ?? message;
    } catch {
      /* garde le message générique */
    }
    throw new Error(message);
  }
}

export interface CreateChallengeReelInput {
  challengeId: string;
  exerciseId: string;
  attemptId: string | null;
  userId: string;
  overlay: VideoOverlay;
  /** Légende (TikTok / repli). */
  caption: string;
  /** Titre YouTube (≤100). */
  ytTitle: string;
  /** Description YouTube (avec #Shorts). */
  ytDescription: string;
  musicAssetId?: string | null;
  platforms: ChallengeReelPlatform[];
}

/**
 * Crée le rendu (carte du défi, 9:16) et l'enfile en APERÇU pour les plateformes
 * choisies. Retourne l'id `youtube_videos` créé (pour suivre le statut).
 */
export function useCreateChallengeReel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateChallengeReelInput): Promise<string> => {
      const row = {
        created_by: input.userId,
        challenge_id: input.challengeId,
        exercise_id: input.exerciseId,
        attempt_id: input.attemptId,
        background_asset_id: null,
        music_asset_id: input.musicAssetId ?? null,
        aspect_ratio: 'portrait',
        duration_seconds: 30,
        loop_background: true,
        overlay: input.overlay,
        yt_title: input.ytTitle,
        yt_description: input.ytDescription,
        yt_tags: ['Shorts', 'JeRaisonne', 'défi'],
        social_caption: input.caption || null,
      };
      const { data, error } = await ytDb
        .from('youtube_videos')
        .insert(row)
        .select('id')
        .maybeSingle();
      if (error) throw new Error(error.message);
      const video = data as { id: string };
      // Validation manuelle : on REND seulement (aperçu) ; publication après clic.
      await enqueueChallengeReel(video.id, input.platforms, 'preview');
      return video.id;
    },
    onSuccess: (videoId) => {
      qc.invalidateQueries({ queryKey: ['youtube-video', videoId] });
    },
  });
}

/** Approuve l'aperçu → publie sur les plateformes choisies (depuis le master rendu). */
export function usePublishChallengeReel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      videoId: string;
      platforms: ChallengeReelPlatform[];
    }): Promise<string> => {
      await enqueueChallengeReel(input.videoId, input.platforms, 'publish');
      return input.videoId;
    },
    onSuccess: (videoId) => {
      qc.invalidateQueries({ queryKey: ['youtube-video', videoId] });
    },
  });
}
